import { BEATS } from '@/story/beats';

/**
 * How long each stage of a beat actually took, for the person holding the keys.
 *
 * ── What this is for ────────────────────────────────────────────────────────
 *
 * *Play Act 1 end to end with a stopwatch* has been the project's top task since
 * session 15 and has never once been done. **Every timing constant in the act was
 * authored by reasoning and none by playing** — `PATIENCE`, `ROLL_PATIENCE`,
 * `TIP_PATIENCE`, `IN_PATIENCE`, `STORM.tell`, `BOARDS_PATIENCE` — and session 29
 * demonstrated what that costs: one person playing for one sitting found five
 * real defects on a tree that had passed `verify` twice.
 *
 * The task did not get done because doing it was expensive. The save and the
 * beat-jump take the replay cost out; this takes the bookkeeping cost out. The
 * player plays, and the game holds the stopwatch.
 *
 * ── Why it renders in the game and not to the console ───────────────────────
 *
 * Because the review device is a phone (D-127). A `console.log` of this data
 * would be free to build and unreadable by the one person it is for, which is
 * the same mistake as a keyboard-only debug affordance. It goes on screen, with
 * a copy button.
 *
 * ── Why real elapsed time and not simulation time ───────────────────────────
 *
 * The question being asked is *"did that drag?"*, and that is a question about
 * the player's afternoon, not about the fixed step. It is fed the RENDER delta
 * and therefore stops while the game is paused, which is right: time spent in
 * the pause menu is not time spent waiting for a stage to end.
 */

export interface TimelineEntry {
  /** The stage id, as `story.debug.stageId` reports it. */
  readonly stage: string;
  /** Seconds from the first frame of the beat to this stage beginning. */
  readonly atSeconds: number;
  /** Seconds this stage lasted. Live for the stage still running. */
  seconds: number;
}

function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

export class PlaythroughTimeline {
  readonly #entries: TimelineEntry[] = [];
  #elapsed = 0;
  #current: string | null = null;

  constructor(private readonly beat: number) {}

  /**
   * Advance the clock and record a stage change.
   *
   * Called once per rendered frame with the render delta. A stage of `null` —
   * before the first stage begins, and after the last one ends — is recorded as
   * a boundary too: the tail after the final stage is exactly the "she is
   * standing on black wondering whether it is over" interval this is meant to
   * measure.
   */
  sample(dt: number, stage: string | null): void {
    if (Number.isFinite(dt) && dt > 0) this.#elapsed += Math.min(dt, 0.5);

    const last = this.#entries[this.#entries.length - 1];
    if (last) last.seconds = this.#elapsed - last.atSeconds;

    if (stage === this.#current) return;
    this.#current = stage;
    this.#entries.push({ stage: stage ?? '—', atSeconds: this.#elapsed, seconds: 0 });
  }

  get entries(): readonly TimelineEntry[] {
    return this.#entries;
  }

  get elapsed(): number {
    return this.#elapsed;
  }

  /**
   * The whole run as text, ready to paste into a message.
   *
   * Two columns that answer two different questions: when a stage started, which
   * locates a complaint in a playthrough, and how long it lasted, which is the
   * number somebody then goes and changes.
   */
  get text(): string {
    const entry = BEATS[this.beat];
    const heading = `beat ${this.beat} — ${entry?.title ?? 'unknown'} · ${clock(this.#elapsed)} total`;
    const rows = this.#entries.map(
      (row) => `  ${clock(row.atSeconds).padStart(5)}  ${row.stage.padEnd(22)} ${row.seconds.toFixed(1)}s`,
    );
    return [heading, ...rows].join('\n');
  }
}
